/**
 * SignalBar — labelled horizontal bar showing a signal's name + score + a v3
 * orbital-matched gauge (gradient track + position indicator pin + zero
 * marker line).
 *
 * Mirrors `BrandEquityVisualizationV3.tsx:1132-1174` exactly:
 *   • Universal red→amber→yellow→light-green→green→teal gradient track
 *   • Zero marker line at 50% (or 100% for Price Deals)
 *   • Dark pin at the score position, with subtle box-shadow
 *   • Score number coloured with the element's ring colour (`colourForScore`)
 *
 * Price Deals is the inverse-scale exception (−100 to 0 → 0% to 100% on the bar).
 */

import React from 'react';
import { LockOutlined } from '@ant-design/icons';
import { formatBipolarScore } from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import type { ScoreNode } from '../types';

const GRADIENT_NORMAL =
  'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)';
const GRADIENT_PRICE_DEALS =
  'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)';

export interface SignalBarProps {
  node: Pick<ScoreNode, 'canonical' | 'display' | 'value' | 'band' | 'locked'>;
  onClick?: () => void;
  /** Truncate to a single line; defaults to true. */
  ellipsis?: boolean;
}

export const SignalBar: React.FunctionComponent<SignalBarProps> = ({
  node,
  onClick,
  ellipsis = true,
}) => {
  const navigate = useScopedNavigate();
  const scoreColour = colourForScore(node.canonical, node.value);
  // Subscription-tier locked element — hide the score behind a lock badge
  // (mirrors the orbital's locked-node treatment).
  const isLocked = !!node.locked;
  // A locked bar upsells instead of opening the (also-locked) drawer.
  const handleClick = isLocked
    ? () => navigate(MARKETER_UPGRADE_ROUTE)
    : onClick;
  const isPriceDeals = node.canonical === 'Price Deals';
  // Price Deals is on a -100..0 scale; everything else -100..+100.
  // Map score → indicator position percentage along the bar.
  const indicatorPosition =
    node.value == null
      ? null
      : isPriceDeals
        ? ((node.value + 100) / 100) * 100
        : ((node.value + 100) / 200) * 100;
  const zeroPosition = isPriceDeals ? 100 : 50;
  const inner = (
    <div style={{ minWidth: 0, width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: '#0F172A',
            flex: '1 1 auto',
            minWidth: 0,
            lineHeight: 1.3,
            whiteSpace: ellipsis ? 'nowrap' : undefined,
            overflow: ellipsis ? 'hidden' : undefined,
            textOverflow: ellipsis ? 'ellipsis' : undefined,
          }}
        >
          {node.display}
        </span>
        {isLocked ? (
          <LockOutlined style={{ fontSize: 13, color: '#9ca3af', flexShrink: 0 }} />
        ) : (
          <span style={{ fontSize: 13, fontWeight: 700, color: scoreColour, flexShrink: 0 }}>
            {formatBipolarScore(node.value)}
          </span>
        )}
      </div>
      {/* v3-matched gauge: gradient track + zero marker + position indicator */}
      {indicatorPosition != null && !isLocked ? (
        <div
          style={{
            position: 'relative',
            height: 14,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 6,
              background: isPriceDeals ? GRADIENT_PRICE_DEALS : GRADIENT_NORMAL,
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {/* Zero marker line */}
            <div
              style={{
                position: 'absolute',
                left: `${zeroPosition}%`,
                top: -1,
                bottom: -1,
                width: 1,
                backgroundColor: 'rgba(0,0,0,0.4)',
                transform: 'translateX(-50%)',
              }}
            />
          </div>
          {/* Position indicator pin */}
          <div
            style={{
              position: 'absolute',
              left: `${indicatorPosition}%`,
              top: 0,
              width: 3,
              height: 14,
              backgroundColor: '#1f2937',
              borderRadius: 2,
              transform: 'translateX(-50%)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          />
        </div>
      ) : isLocked ? (
        // Locked: flat grey track (no gradient/pin) so the row reads as a
        // measured-but-locked element rather than looking blank.
        <div style={{ display: 'flex', alignItems: 'center', height: 14 }}>
          <div
            style={{
              width: '100%',
              height: 6,
              background: '#E5E7EB',
              borderRadius: 3,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          />
        </div>
      ) : (
        <div style={{ height: 14 }} />
      )}
    </div>
  );

  if (handleClick) {
    return (
      <button
        type="button"
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: '9px 4px',
          cursor: 'pointer',
          borderRadius: 10,
        }}
      >
        {inner}
      </button>
    );
  }
  return <div style={{ padding: '9px 4px' }}>{inner}</div>;
};
